import { yupResolver } from '@hookform/resolvers/yup';
import { NutritionUnitMasterAddPayload } from 'models';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiaTimesSolid } from 'react-icons/lia';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import useNutritionUnitMaster from '../hooks/useNutritionUnitMaster';

interface NutritionUnitMasterFormProps {
  nextDisplayOrder: number;
  onClose: () => void;
  onReflect?: () => void;
}

function NutritionUnitMasterForm({ nextDisplayOrder, onClose, onReflect }: NutritionUnitMasterFormProps) {
  const { t } = useTranslation();

  const { addNutritionUnitMaster } = useNutritionUnitMaster();

  // schema validation
  const schema = yup
    .object({
      nutrition_unit_name: yup.string().required(t('NutritionUnitMaster.warn_field_required')),
      display_order: yup
        .number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() === '') {
            return undefined;
          }
          return value;
        })
        .min(1, t('NutritionUnitMaster.warn_number_required'))
        .integer(t('NutritionUnitMaster.warn_number_required'))
        .required(t('NutritionUnitMaster.warn_number_required')),
    })
    .required();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<NutritionUnitMasterAddPayload>({
    resolver: yupResolver(schema),
  });
  const onSubmit = async (payload: NutritionUnitMasterAddPayload) => {
    const { data, error } = await addNutritionUnitMaster(payload);
    if (data) {
      toast.success(data?.message);
      onReflect?.();
      onClose();
    } else {
      if (error && error.error) {
        const values = getValues();
        const fieldNames = Object.keys(values);
        toast.error(<div dangerouslySetInnerHTML={{ __html: error.message || t('App.unexpected_error') }}></div>);
        for (const key of Object.keys(error.error)) {
          if (fieldNames.includes(key)) {
            setError(key as keyof NutritionUnitMasterAddPayload, { type: 'custom', message: error.error[key] });
          }
        }
      }
    }
  };

  return (
    <Modal
      show
      size="lg"
      dialogClassName="nutrition-unit-master-form-modal"
      backdrop="static"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={onClose}
    >
      <Modal.Header className="border-bottom-0 justify-content-end">
        <Button variant="primary" className="btn--auto" onClick={onClose}>
          <LiaTimesSolid size={16} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Form.Group as={Row} className="mb-3" controlId="nutrition_unit_name">
            <Form.Label column sm="3" className="text-end">
              {t('NutritionUnitMaster.nutrition_unit_name')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('nutrition_unit_name')}
                type="text"
                placeholder=""
                isInvalid={!!errors.nutrition_unit_name}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{errors.nutrition_unit_name?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="display_order">
            <Form.Label column sm="3" className="text-end">
              {t('NutritionUnitMaster.display_order')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('display_order')}
                className="text-end"
                style={{ width: '80px' }}
                type="number"
                placeholder=""
                defaultValue={nextDisplayOrder}
                min={1}
                isInvalid={!!errors.display_order}
              />
              <Form.Control.Feedback type="invalid">{errors.display_order?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <div className="text-center mt-5">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="d-inline-flex gap-1 justify-content-center btn btn-primary align-items-center"
            >
              <span>{t('NutritionUnitMaster.add')}</span>
              {isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default NutritionUnitMasterForm;
